"use client";
import { Button } from "@/components/ui/button";
import { formatToRupee } from "@/lib/internalApis";
import useFlightBookingStore from "@/lib/useFlightBookingStore";
import { logFirebaseEvent } from "@/utils/firebaseAnalytics";
import { Loader2, Info, ArrowRight } from "lucide-react";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface FlightDetail {
  aI: Airline;
  fN: string;
}

interface Airline {
  code: string;
  name: string;
}

interface Flight {
  fD: FlightDetail;
  da: Airport;
  aa: Airport;
  dt: string;
  at: string;
  duration: number;
}

interface Airport {
  city: string;
  code: string;
}

interface Fare {
  id: string;
  fd: {
    ADULT: {
      fC: {
        TF: number;
      };
    };
  };
}

const formatDateAndTime = (dateTimeString: string) => {
  const dateObj = new Date(dateTimeString);
  const day = dateObj.getDate();
  const month = dateObj.toLocaleString("default", { month: "short" });
  const weekday = dateObj.toLocaleString("default", { weekday: "short" });
  const formattedDate = `${weekday}, ${day} ${month}`;
  const hours = dateObj.getHours().toString().padStart(2, "0");
  const minutes = dateObj.getMinutes().toString().padStart(2, "0");
  const formattedTime = `${hours}:${minutes}`;
  return { formattedDate, formattedTime };
};

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

const SelectedFlight = ({
  routeData,
  manualBlockId,
  manualBlockData,
}: {
  routeData?: any;
  manualBlockId?: string;
  manualBlockData?: any;
}) => {
  const { push } = useRouter();
  const search: any = useSearchParams();
  const [isLoading, setIsLoading] = React.useState(false);

  const {
    selectedReturnFlights,
    selectedMulticityFlights,
    storeSearchParam,
  }: any = useFlightBookingStore();

  const handleBookFlight = () => {
    setIsLoading(true);
    const fareIds = selectedMulticityFlights
      .map((flight: any) => flight.fare?.id)
      .filter(Boolean)
      .join(",");

    if (fareIds) {
      const isReschedule = search.get("reschedule") === "true";
      const oldBookingId = search.get("oldBookingId");
      const params = new URLSearchParams();
      if (isReschedule) {
        params.set("reschedule", "true");
        if (oldBookingId) params.set("oldBookingId", oldBookingId);
        const paxIds = search.get("paxIds");
        if (paxIds) params.set("paxIds", paxIds);
      }

      let url = `/flight-review/${fareIds}`;
      if (manualBlockId) {
        url += `/manual-block/${manualBlockId}`;
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      push(url);
      storeSearchParam(search);
      logFirebaseEvent("MultiCityFlight_Booking", {
        fareIdentifier: fareIds,
      });
    } else {
      console.error("No valid fare selected.");
    }
  };

  const multiCityTotalFare = selectedMulticityFlights.reduce(
    (acc: number, flight: any) => acc + (flight?.fare?.fd?.ADULT?.fC?.TF || 0),
    0,
  );

  const totalFare =
    selectedReturnFlights?.onword?.fare && selectedReturnFlights?.return?.fare
      ? selectedReturnFlights.onword.fare.fd.ADULT.fC.TF +
        selectedReturnFlights.return.fare.fd.ADULT.fC.TF
      : multiCityTotalFare;

  const FareBreakupItem = ({
    flight,
    fare,
    isReturn = false,
  }: {
    flight: Flight;
    fare: Fare;
    isReturn?: boolean;
  }) => {
    const { formattedDate, formattedTime: depTime } = formatDateAndTime(
      flight.dt,
    );
    const { formattedTime: arrTime } = formatDateAndTime(flight.at);

    return (
      <div className="mb-6 last:mb-0">
        <h4 className="text-[14px] font-semibold text-gray-800 mb-2">
          {flight.da.city} - {flight.aa.city}, {formattedDate}
        </h4>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-[40px] h-[40px] bg-white rounded flex items-center justify-center border p-1">
              <Image
                src={`/AirlinesLogo/${flight.fD.aI.code}.png`}
                alt={flight.fD.aI.name}
                width={32}
                height={32}
              />
            </div>
            <div>
              <div className="flex items-center text-[15px] font-bold text-gray-900">
                <span>{flight.da.code}</span>
                <ArrowRight className="w-4 h-4 mx-2 text-gray-400" />
                <span>{flight.aa.code}</span>
              </div>
              <div className="text-[12px] text-gray-500 font-medium">
                {depTime} - {arrTime}
              </div>
            </div>
          </div>
          <div className="text-[16px] font-bold text-gray-900">
            {formatToRupee(fare.fd.ADULT.fC.TF)}
          </div>
        </div>
      </div>
    );
  };

  const FareBreakupSheetContent = () => (
    <>
      <SheetHeader className="mt-2 mb-6 border-b pb-4">
        <SheetTitle className="text-gray-900 text-left text-xl font-bold">
          Fare Breakup
        </SheetTitle>
      </SheetHeader>
      <div className="pb-8">
        {selectedReturnFlights?.onword?.flight && (
          <FareBreakupItem
            flight={selectedReturnFlights.onword.flight}
            fare={selectedReturnFlights.onword.fare}
          />
        )}
        {selectedReturnFlights?.return?.flight && (
          <div className="border-t pt-6 mt-6">
            <FareBreakupItem
              flight={selectedReturnFlights.return.flight}
              fare={selectedReturnFlights.return.fare}
              isReturn
            />
          </div>
        )}
        {selectedMulticityFlights.map((selectedFlight: any, index: number) => (
          <div key={index} className={index > 0 ? "border-t pt-6 mt-6" : ""}>
            <FareBreakupItem
              flight={selectedFlight.sI}
              fare={selectedFlight.fare}
            />
          </div>
        ))}
      </div>
    </>
  );

  const FlightDetailsContent = () => {
    const showMoreCount =
      selectedMulticityFlights.length > 2
        ? selectedMulticityFlights.length - 2
        : 0;
    const desktopMulticity = selectedMulticityFlights.slice(0, 2);

    return (
      <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-2 w-full ">
        {/* One Way Flight */}
        {selectedReturnFlights?.onword?.flight?.fD && (
          <div className="w-full md:min-w-[42%] inline-block relative md:pr-[30px]">
            <div className="flex w-full items-center text-white">
              <div className="flex w-4/12 md:w-3/12">
                <div className="w-[30px] h-[30px]">
                  <Image
                    src={`/AirlinesLogo/${selectedReturnFlights?.onword?.flight?.fD?.aI?.code}.png`}
                    alt={selectedReturnFlights?.onword?.flight.fD?.aI?.name}
                    width={40}
                    height={40}
                    className="mr-3"
                  />
                </div>
                <div className="text-[12px] pl-[7px]">
                  <h3 className="font-bold text-white">
                    {selectedReturnFlights?.onword?.flight.fD.aI.name}
                  </h3>
                  <p className="text-[11px] text-[#adb5bd]">
                    {selectedReturnFlights?.onword?.flight.fD.fN}
                  </p>
                </div>
              </div>
              <div className="flex justify-between items-center w-5/12 md:w-6/12">
                <div className="text-center flex-1">
                  <p className="font-bold text-[13px] text-white">
                    {
                      formatDateAndTime(selectedReturnFlights.onword.flight.dt)
                        .formattedTime
                    }
                  </p>
                  <p className="text-[11px] text-[#9b9b9b]">
                    {selectedReturnFlights.onword.flight.da.code}
                  </p>
                </div>
                <div className="flex-1">
                  <div className="text-center flex items-center justify-center">
                    <div className="w-10 md:w-14 h-px my-[0.5rem] relative border border-dashed border-[#dee2e6] before:content-['✈'] before:font-black before:-translate-x-2/4 before:-translate-y-2/4 before:text-[#a0abb8] before:absolute before:left-2/4 before:top-2/4">
                      <div className="bg-white absolute -translate-y-2/4 w-2 h-2 md:w-2.5 md:h-2.5 border rounded-full border-solid border-[#dee2e6] -left-0.5 top-2/4"></div>
                      <div className="bg-[#dee2e6] absolute -translate-y-2/4 w-2 h-2 md:w-2.5 md:h-2.5 border rounded-full border-solid border-[#dee2e6] -right-0.5 top-2/4"></div>
                    </div>
                  </div>
                </div>
                <div className="text-center flex-1">
                  <p className="font-bold text-[13px] text-white">
                    {
                      formatDateAndTime(selectedReturnFlights.onword.flight.at)
                        .formattedTime
                    }
                  </p>
                  <p className="text-[11px] text-[#9b9b9b]">
                    {selectedReturnFlights.onword.flight.aa.code}
                  </p>
                </div>
              </div>
              <div className="w-3/12 md:after:absolute md:after:content-[''] md:after:h-5 md:after:w-px md:after:right-[15px] md:after:top-2 md:after:bg-[#ccc]">
                <h5 className="font-medium text-right md:text-left">
                  {formatToRupee(
                    selectedReturnFlights.onword.fare.fd.ADULT.fC.TF,
                  )}
                </h5>
              </div>
            </div>
          </div>
        )}

        {/* Return Flight */}
        {selectedReturnFlights?.return?.flight?.fD && (
          <div className="w-full md:min-w-[42%] inline-block relative md:pr-[30px]">
            <div className="flex items-center text-white w-full">
              <div className="flex w-4/12 md:w-3/12">
                <div className="w-[30px] h-[30px]">
                  <Image
                    src={`/AirlinesLogo/${selectedReturnFlights?.return?.flight?.fD?.aI?.code}.png`}
                    alt={selectedReturnFlights?.return?.flight?.fD?.aI?.name}
                    width={40}
                    height={40}
                    className="mr-3"
                  />
                </div>
                <div className="text-[12px] pl-[7px]">
                  <h3 className="font-bold text-white">
                    {selectedReturnFlights?.return?.flight?.fD?.aI?.name}
                  </h3>
                  <p className="text-[11px] text-[#adb5bd]">
                    {selectedReturnFlights?.return?.flight?.fD?.fN}
                  </p>
                </div>
              </div>
              <div className="flex justify-between items-center w-5/12  md:w-6/12">
                <div className="text-center flex-1">
                  <p className="font-bold text-[13px] text-white">
                    {
                      formatDateAndTime(selectedReturnFlights.return.flight.dt)
                        .formattedTime
                    }
                  </p>
                  <p className="text-[11px] text-[#9b9b9b]">
                    {selectedReturnFlights.return.flight.da.code}
                  </p>
                </div>
                <div className="flex-1">
                  <div className="text-center flex items-center justify-center">
                    <div className="w-10 lg:w-14 h-px my-[0.5rem] relative border border-dashed border-[#dee2e6] before:content-['✈'] before:font-black before:-translate-x-2/4 before:-translate-y-2/4 before:text-[#a0abb8] before:absolute before:left-2/4 before:top-2/4">
                      <div className="bg-white absolute -translate-y-2/4 w-2 h-2 lg:w-2.5 lg:h-2.5 border rounded-full border-solid border-[#dee2e6] -left-0.5 top-2/4"></div>
                      <div className="bg-[#dee2e6] absolute -translate-y-2/4 w-2 h-2 lg:w-2.5 lg:h-2.5 border rounded-full border-solid border-[#dee2e6] -right-0.5 top-2/4"></div>
                    </div>
                  </div>
                </div>
                <div className="text-center flex-1">
                  <p className="font-bold text-[13px] text-white">
                    {
                      formatDateAndTime(selectedReturnFlights.return.flight.at)
                        .formattedTime
                    }
                  </p>
                  <p className="text-[11px] text-[#9b9b9b]">
                    {selectedReturnFlights.return.flight.aa.code}
                  </p>
                </div>
              </div>
              <div className="w-3/12 lg:after:absolute lg:after:content-[''] lg:after:h-5 lg:after:w-px lg:after:right-[15px] lg:after:top-2 lg:after:bg-[#ccc]">
                <h5 className="font-medium text-right lg:text-left">
                  {formatToRupee(
                    selectedReturnFlights.return.fare.fd.ADULT.fC.TF,
                  )}
                </h5>
              </div>
            </div>
          </div>
        )}

        {/* Multi City Flights */}
        {desktopMulticity.length > 0 && (
          <div className="w-full overflow-y-auto max-h-[60vh] md:max-h-full">
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-4">
              {desktopMulticity.map((selectedFlights: any, index: number) => {
                const fD = selectedFlights?.sI;
                return (
                  <div
                    className="flex items-center text-white w-full lg:w-[400px]"
                    key={index}
                  >
                    <div className="flex w-4/12 lg:w-auto">
                      <div className="w-[30px] h-[30px]">
                        <Image
                          src={`/AirlinesLogo/${selectedFlights?.sI?.fD?.aI?.code}.png`}
                          alt={selectedFlights?.sI?.fD?.aI?.name}
                          width={40}
                          height={40}
                          className="mr-3"
                        />
                      </div>
                      <div className="text-[12px] pl-[7px]">
                        <h3 className="font-bold text-white">
                          {selectedFlights?.sI?.fD?.aI?.name}
                        </h3>
                        <p className="text-[11px] text-[#adb5bd]">
                          {selectedFlights?.sI?.fD?.fN}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center w-5/12 lg:w-6/12">
                      <div className="text-center flex-1">
                        <p className="font-bold text-[13px] text-white">
                          {formatDateAndTime(fD?.dt).formattedTime}
                        </p>
                        <p className="text-[11px] text-[#9b9b9b]">
                          {fD?.da?.code}
                        </p>
                      </div>
                      <div className="flex-1">
                        <div className="text-center flex flex-col items-center">
                          <div className="w-10 lg:w-14 h-px my-[0.5rem] relative border border-dashed border-[#dee2e6] before:content-['✈'] before:font-black before:-translate-x-2/4 before:-translate-y-2/4 before:text-[#a0abb8] before:absolute before:left-2/4 before:top-2/4">
                            <div className="bg-white absolute -translate-y-2/4 w-2 h-2 lg:w-2.5 lg:h-2.5 border rounded-full border-solid border-[#dee2e6] -left-0.5 top-2/4"></div>
                            <div className="bg-[#dee2e6] absolute -translate-y-2/4 w-2 h-2 lg:w-2.5 lg:h-2.5 border rounded-full border-solid border-[#dee2e6] -right-0.5 top-2/4"></div>
                          </div>
                        </div>
                      </div>
                      <div className="text-center flex-1">
                        <p className="font-bold text-[13px] text-white">
                          {formatDateAndTime(fD?.at).formattedTime}
                        </p>
                        <p className="text-[11px] text-[#9b9b9b]">
                          {fD?.aa?.code}
                        </p>
                      </div>
                    </div>
                    <div className="w-3/12 lg:after:absolute lg:after:content-[''] lg:after:h-5 lg:after:w-px lg:after:right-[15px] lg:after:top-2 lg:after:bg-[#ccc]">
                      <h5 className="font-medium text-right lg:text-left">
                        {selectedFlights?.fare?.fd?.ADULT?.fC?.TF
                          ? formatToRupee(selectedFlights.fare.fd.ADULT.fC.TF)
                          : "N/A"}
                      </h5>
                    </div>
                  </div>
                );
              })}
              {showMoreCount > 0 && (
                <Sheet>
                  <SheetTrigger asChild>
                    <button className="text-theme_color text-sm font-bold hover:underline ml-2 whitespace-nowrap">
                      + {showMoreCount} more
                    </button>
                  </SheetTrigger>
                  <SheetContent
                    side="left"
                    className="bg-white text-gray-900 border-none w-full overflow-y-auto px-6 pb-10"
                  >
                    <FareBreakupSheetContent />
                  </SheetContent>
                </Sheet>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="text-white fixed bottom-0 bg-black w-full p-3 lg:p-[18px_10px_10px] z-[100]">
      <div className="container mx-auto">
        <div className="flex items-center justify-between">
          {/* Desktop Detail View */}
          <div className="hidden md:block flex-1 overflow-x-auto">
            <div className="min-w-[1000px]">
              <FlightDetailsContent />
            </div>
          </div>

          {/* Mobile Simplified View */}
          <div className="flex md:hidden items-center space-x-4">
            <h5 className="font-bold text-lg text-white">
              {formatToRupee(totalFare)}
            </h5>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white">
                  <Info className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="bg-white text-gray-900 border-none w-full overflow-y-auto px-6 pb-10"
              >
                <FareBreakupSheetContent />
              </SheetContent>
            </Sheet>
          </div>

          {/* Combined Total & Book Button for Desktop */}
          <div className="flex items-center space-x-4 ml-4">
            <div className="hidden md:block text-right mr-4">
              <p className="text-xs text-gray-400">Total Fare</p>
              <h5 className="font-bold text-lg text-white">
                {formatToRupee(totalFare)}
              </h5>
            </div>

            {selectedReturnFlights?.return?.flight ? (
              <Button
                className="rounded px-5 py-2.5 !bg-theme_color text-white flex flex-col items-center"
                disabled={isLoading}
                onClick={() => {
                  setIsLoading(true);
                  const fareIds = `${selectedReturnFlights?.onword?.fare?.id},${selectedReturnFlights?.return?.fare?.id}`;
                  const isReschedule = search.get("reschedule") === "true";
                  const oldBookingId = search.get("oldBookingId");
                  const params = new URLSearchParams();
                  if (isReschedule) {
                    params.set("reschedule", "true");
                    if (oldBookingId) params.set("oldBookingId", oldBookingId);
                    const paxIds = search.get("paxIds");
                    if (paxIds) params.set("paxIds", paxIds);
                  }

                  let url = `/flight-review/${fareIds}`;
                  if (manualBlockId) {
                    url += `/manual-block/${manualBlockId}`;
                  }
                  if (params.toString()) {
                    url += `?${params.toString()}`;
                  }
                  push(url);
                  storeSearchParam(search);
                  logFirebaseEvent("RoundTripFlight_Booking", {
                    fareIdentifier: fareIds,
                  });
                }}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <Loader2 className="animate-spin" />
                    <span className="text-[12px] font-bold">Book Now</span>
                  </div>
                ) : (
                  <>
                    <span className="text-[12px] font-bold">Book Now</span>
                  </>
                )}
              </Button>
            ) : (
              <Button
                className="rounded px-5 py-2.5 !bg-theme_color text-white flex flex-col items-center"
                disabled={isLoading}
                onClick={() => handleBookFlight()}
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <>
                    <span className=" font-bold">Book Now</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectedFlight;
